import { Link, createFileRoute } from '@tanstack/react-router'
import { getAdjacentItems } from '@/features/docs/config/navigation'
import { DocsBreadcrumb } from '@/features/docs/components/DocsBreadcrumb'
import { DocsCopyButton } from '@/features/docs/components/DocsCopyButton'
import { generateLinks, generateMetaTags } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'

const SLUG = 'api-reference/generations'
const TITLE = 'Generations API Reference'

export const Route = createFileRoute('/docs/api-reference/generations')({
  head: () => ({
    meta: generateMetaTags({
      title: `${TITLE} - ModelStack Docs`,
      description:
        'Complete documentation for the ModelStack Generations API — create, list, and manage AI-generated images and videos.',
      canonical: `/docs/${SLUG}`,
    }),
    links: generateLinks({ canonical: `/docs/${SLUG}` }),
    scripts: [
      {
        type: 'application/ld+json',
        children: renderJsonLd(
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Docs', url: '/docs' },
            { name: TITLE, url: `/docs/${SLUG}` },
          ]) as any,
        ),
      },
    ],
  }),
  component: GenerationsApiPage,
})

function GenerationsApiPage() {
  const { prev, next } = getAdjacentItems(SLUG)

  return (
    <div className="flex gap-0">
      {/* Main content */}
      <div className="flex-1 min-w-0 px-8 py-10 max-w-3xl">
        <DocsBreadcrumb slug={SLUG} />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)] tracking-[-0.03em] md:text-4xl">
            Generations API Reference
          </h1>
          <DocsCopyButton slug={SLUG} />
        </div>
        <p className="text-[var(--color-text-secondary)] text-lg mb-10">
          Complete documentation for the ModelStack Generations API — create, list, and manage AI-generated images and videos.
        </p>

        {/* Overview */}
        <section id="overview" className="mb-10">
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-3">Overview</h2>
          <div className="prose-custom space-y-4">
            <p className="text-[var(--color-text-secondary)] leading-relaxed">
              The Generations API provides a unified interface for AI image and video generation across
              multiple providers. All requests require authentication with an API key.
            </p>

            <h3 className="text-base font-semibold text-[var(--color-text-primary)] mt-6 mb-2">Base URL</h3>
            <CodeBlock code="https://api.modelstack.cc" />

            <h3 className="text-base font-semibold text-[var(--color-text-primary)] mt-6 mb-2">Authentication</h3>
            <p className="text-[var(--color-text-secondary)] mb-3">
              Include your API key in the <code className="text-sm bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded">Authorization</code> header:
            </p>
            <CodeBlock code={`Authorization: Bearer sk_YOUR_API_KEY`} />

            <h3 className="text-base font-semibold text-[var(--color-text-primary)] mt-6 mb-2">Rate Limits</h3>
            <p className="text-[var(--color-text-secondary)]">
              There is currently no enforced rate limit on the Generations API — don't build against a
              specific requests-per-minute figure. Your effective throughput is bounded by your account
              balance, since every generation is billed on creation.
            </p>
          </div>
        </section>

        {/* Endpoint: Create Generation */}
        <section id="create-generation" className="mb-10 scroll-mt-20">
          <EndpointMethodBadge method="POST" />
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-1">Create Generation</h2>
          <code className="text-base text-[var(--color-text-primary)] font-mono">/v1/generations</code>
          <p className="text-[var(--color-text-secondary)] mt-3 mb-4">
            Create a new image or video generation. Returns immediately with a pending generation — poll the
            generation ID to get the result.
          </p>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 uppercase tracking-wider">Request Body</h3>
          <div className="glass-panel rounded-2xl overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-bg-tertiary)]/50 border-b border-[var(--color-border)]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Field</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Required</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                <FieldRow name="type" type="string" required description="Generation type, e.g. TEXT_TO_IMAGE, TEXT_TO_VIDEO, IMAGE_TO_IMAGE" />
                <FieldRow name="prompt" type="string" required description="Text prompt describing the desired output" />
                <FieldRow name="model" type="string" required description="Model identifier, e.g. google/nano-banana-2-lite" />
                <FieldRow name="modelSettings" type="object" description="Model-specific parameters (aspect ratio, resolution, duration, etc.)" />
              </tbody>
            </table>
          </div>

          <CodeBlock code={`curl -X POST "https://api.modelstack.cc/v1/generations" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "TEXT_TO_IMAGE",
    "prompt": "A serene mountain landscape at sunset",
    "model": "google/nano-banana-2-lite",
    "modelSettings": {
      "aspect_ratio": "16:9"
    }
  }'`}
          />

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mt-4 mb-2 uppercase tracking-wider">Response</h3>
          <CodeBlock code={`{ "data": { "id": "gen_abc123" } }`} />
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
            Only the <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">id</code> is returned — poll <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">GET /v1/generations/{'{id}'}</code> for status and output.
          </p>
        </section>

        {/* Endpoint: Get Generation */}
        <section id="get-generation" className="mb-10 scroll-mt-20">
          <EndpointMethodBadge method="GET" />
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-1">Get Generation</h2>
          <code className="text-base text-[var(--color-text-primary)] font-mono">/v1/generations/{'{id}'}</code>
          <p className="text-[var(--color-text-secondary)] mt-3 mb-4">
            Get the status and result of a specific generation. Poll this endpoint to check when generation is complete.
          </p>

          <CodeBlock code={`curl "https://api.modelstack.cc/v1/generations/gen_abc123" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"`}
          />

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mt-4 mb-2 uppercase tracking-wider">Response</h3>
          <CodeBlock code={`{
  "data": {
    "id": "gen_abc123",
    "status": "completed",
    "creditsUsed": 40,
    "outputMediaUrls": ["media/user_123/gen_abc123/0.png"],
    "createdAt": "2026-01-01T00:00:00.000Z",
    "completedAt": "2026-01-01T00:00:12.000Z"
  }
}`} />
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
            <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">status</code> is one of <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">pending</code>, <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">processing</code>, <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">completed</code>, <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">failed</code>, or <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">deleted</code>.
            <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">outputMediaUrls</code> holds storage keys, not downloadable URLs — exchange each key for a signed URL via <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">GET /v1/media/presigned-url?key=...</code> (expires after 1 hour).
          </p>
        </section>

        {/* Endpoint: List Generations */}
        <section id="list-generations" className="mb-10 scroll-mt-20">
          <EndpointMethodBadge method="GET" />
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-1">List Generations</h2>
          <code className="text-base text-[var(--color-text-primary)] font-mono">/v1/generations</code>
          <p className="text-[var(--color-text-secondary)] mt-3 mb-4">
            List all generations with pagination and optional status filtering.
          </p>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 uppercase tracking-wider">Query Parameters</h3>
          <div className="glass-panel rounded-2xl overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-bg-tertiary)]/50 border-b border-[var(--color-border)]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Parameter</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Default</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-[var(--color-text-primary)]">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                <FieldRow name="limit" type="number" defaultVal="25" description="Max records (max 100)" />
                <FieldRow name="offset" type="number" defaultVal="0" description="Pagination offset" />
                <FieldRow name="status" type="string" description="Filter: pending | processing | completed | failed" />
              </tbody>
            </table>
          </div>

          <CodeBlock code={`curl "https://api.modelstack.cc/v1/generations?limit=10&status=completed" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"`}
          />
        </section>

        {/* Endpoint: Delete Generation */}
        <section id="delete-generation" className="mb-10 scroll-mt-20">
          <EndpointMethodBadge method="DELETE" />
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-1">Delete Generation</h2>
          <code className="text-base text-[var(--color-text-primary)] font-mono">/v1/generations/{'{id}'}</code>
          <p className="text-[var(--color-text-secondary)] mt-3 mb-4">
            Delete a completed or failed generation to free up storage.
          </p>

          <CodeBlock code={`curl -X DELETE "https://api.modelstack.cc/v1/generations/gen_abc123" \\
  -H "Authorization: Bearer sk_YOUR_API_KEY"`}
          />

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mt-4 mb-2 uppercase tracking-wider">Response</h3>
          <CodeBlock code={`{ "success": true }`} />
        </section>

        {/* Error handling section */}
        <section id="errors" className="mb-10 scroll-mt-20">
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-3">Error Handling</h2>
          <p className="text-[var(--color-text-secondary)] mb-4">
            Error responses are <strong>not</strong> uniformly shaped — the shape depends on which layer
            rejects the request.
          </p>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 uppercase tracking-wider">Rejected before reaching the app</h3>
          <p className="text-[var(--color-text-secondary)] mb-3">
            Auth failures and insufficient-balance pre-checks return a nested shape:
          </p>
          <CodeBlock code={`{ "error": { "message": "Authentication failed", "type": "invalid_api_key" } }`} />
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1 mb-4">HTTP 401.</p>
          <CodeBlock code={`{
  "error": { "message": "Insufficient balance", "type": "insufficient_balance", "code": "insufficient_balance" },
  "balance": { "available": 0, "required": 10000 }
}`} />
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1 mb-6">
            HTTP 402. This pre-check uses a rough flat cost estimate, not the exact per-request price — it can
            pass and still have the real balance check fail later (surfacing as a 500, see below).
          </p>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 uppercase tracking-wider">Rejected by the app</h3>
          <p className="text-[var(--color-text-secondary)] mb-3">
            Validation/lookup failures once the request reaches the app return a flat string, not a nested object:
          </p>
          <CodeBlock code={`{ "error": "type, prompt, and model are required" }`} />
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1 mb-6">
            HTTP 400. Similarly, <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">{'{ "error": "Unauthorized" }'}</code> for 401, and <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">{'{ "error": "<message>" }'}</code> for 404.
          </p>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 uppercase tracking-wider">Known edge-case behavior</h3>
          <ul className="space-y-2 text-[var(--color-text-secondary)] text-sm">
            <li className="flex items-start gap-2">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--color-text-tertiary)] shrink-0" />
              <span><code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">GET /v1/generations/{'{id}'}</code> for a nonexistent id returns HTTP 200 with <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">{'{ "data": null }'}</code>, not a 404.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--color-text-tertiary)] shrink-0" />
              <span><code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">DELETE /v1/generations/{'{id}'}</code> on a pending/processing generation returns HTTP 500, not a 400 — only completed/failed generations can be deleted.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--color-text-tertiary)] shrink-0" />
              <span>Unexpected failures (including the real balance check failing after the pre-check estimate passed) return <code className="text-xs bg-[var(--color-bg-tertiary)] px-1.5 py-0.5 rounded-full">{'{ "error": "Internal server error" }'}</code> (500) rather than a specific 4xx.</span>
            </li>
          </ul>
        </section>

        {/* Prev/Next navigation */}
        {(prev || next) && (
          <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-[var(--color-border)]">
            {prev ? (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${prev.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group max-w-[45%]"
              >
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_back
                </span>
                <div className="text-left min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">
                    Previous
                  </div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {next && (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${next.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group ml-auto max-w-[45%]"
              >
                <div className="text-right min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">
                    Next
                  </div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {next.title}
                  </div>
                </div>
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_forward
                </span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right ToC rail */}
      <div className="hidden xl:block w-56 shrink-0 px-4 py-10">
        <nav className="sticky top-24 space-y-3">
          <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]">
            On this page
          </p>
          <ul className="space-y-0.5 text-sm">
            <li>
              <a href="#overview" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Overview
              </a>
            </li>
            <li>
              <a href="#create-generation" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Create Generation
              </a>
            </li>
            <li>
              <a href="#get-generation" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Get Generation
              </a>
            </li>
            <li>
              <a href="#list-generations" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                List Generations
              </a>
            </li>
            <li>
              <a href="#delete-generation" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Delete Generation
              </a>
            </li>
            <li>
              <a href="#errors" className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]">
                Error Handling
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

/* ── Sub-components ── */

function CodeBlock({ code }: { code: string }) {
  return (
    <div className="bg-[var(--color-bg-tertiary)] p-4 rounded-2xl overflow-x-auto border border-[var(--color-border)]">
      <pre className="text-sm text-[var(--color-text-primary)] font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  )
}

const METHOD_BADGE_COLORS: Record<string, string> = {
  POST: 'bg-[var(--color-success-bg)] text-[var(--color-success-text)] border-[var(--color-success-border)]',
  GET: 'bg-[var(--color-info-bg)] text-[var(--color-info-text)] border-[var(--color-info-border)]',
  DELETE: 'bg-[var(--color-error-bg)] text-[var(--color-error-text)] border-[var(--color-error-border)]',
}

function EndpointMethodBadge({ method }: { method: string }) {
  return (
    <span className={`inline-block px-3 py-0.5 rounded-full font-mono text-sm font-semibold border mb-2 backdrop-blur-md ${METHOD_BADGE_COLORS[method] ?? ''}`}>
      {method}
    </span>
  )
}

function FieldRow({ name, type, required, defaultVal, description }: {
  name: string
  type: string
  required?: boolean
  defaultVal?: string
  description: string
}) {
  return (
    <tr className="text-[var(--color-text-secondary)]">
      <td className="px-4 py-2.5 font-mono text-[var(--color-info-text)]">{name}</td>
      <td className="px-4 py-2.5 font-mono text-xs">{type}</td>
      <td className="px-4 py-2.5">{required ? <span className="text-[var(--color-error-text)]">Yes</span> : <span className="text-[var(--color-text-tertiary)]">No</span>}</td>
      <td className="px-4 py-2.5">
        {description}
        {defaultVal && <span className="text-[var(--color-text-tertiary)] ml-1">(default: {defaultVal})</span>}
      </td>
    </tr>
  )
}
